import * as DOM from '../services/dom.ts';
import {scrollAndHighlightElement} from '../services/util.ts';
import {Component} from './component';

function toggleAnchorHighlighting(elementId, shouldHighlight) {
    const themeClasses = ['theme-0', 'theme-1', 'theme-2', 'theme-3', 'theme-4', 'theme-5'];
    const target = document.getElementById(elementId);
    const worthersColor = target?.closest('.comp-section')?.getAttribute('data-doc-color') || 'theme-3';

    DOM.forEach(`#page-navigation a[href="#${elementId}"], .sidebar-page-nav a[href="#${elementId}"]`, anchor => {
        anchor.closest('li')?.classList.toggle('current-heading', shouldHighlight);

        if (!anchor.closest('.sidebar-page-nav.doc-nav')) {
            return;
        }

        anchor.classList.remove(...themeClasses);

        if (shouldHighlight) {
            anchor.classList.remove('theme-3-darker');
            anchor.classList.add('current-heading', worthersColor);
            return;
        }

        anchor.classList.remove('current-heading');
        anchor.classList.add('theme-3-darker');
    });
}

function headingVisibilityChange(entries) {
    for (const entry of entries) {
        const isVisible = (entry.intersectionRatio >= 1);
        toggleAnchorHighlighting(entry.target.id, isVisible);
    }
}

function addNavObserver(headings) {
    // Setup the intersection observer.
    const intersectOpts = {
        rootMargin: '0px 0px 0px 0px',
        threshold: 1.0,
    };
    const pageNavObserver = new IntersectionObserver(headingVisibilityChange, intersectOpts);

    // observe each heading
    for (const heading of headings) {
        pageNavObserver.observe(heading);
    }
}

export class PageDisplay extends Component {

    setup() {
        this.container = this.$el;
        this.pageId = this.$opts.pageId;

        window.importVersioned('code').then(Code => Code.highlight());
        this.setupSectionAccordions();
        this.setupNavHighlighting();

        // Check the hash on load
        if (window.location.hash) {
            const text = window.location.hash.replace(/%20/g, ' ').substring(1);
            this.goToText(text);
        }

        // Sidebar page nav click event
        const sidebarPageNav = document.querySelector('.sidebar-page-nav');
        if (sidebarPageNav) {
            DOM.onChildEvent(sidebarPageNav, 'a', 'click', (event, child) => {
                event.preventDefault();
                const triLayout = window.$components.first('tri-layout');
                if (triLayout) {
                    triLayout.showContent();
                }
                const contentId = child.getAttribute('href').substr(1);
                this.goToText(contentId);
                window.history.pushState(null, null, `#${contentId}`);
            });
        }
    }

    goToText(text) {
        const idElem = document.getElementById(text);

        DOM.forEach('.page-content [data-highlighted]', elem => {
            elem.removeAttribute('data-highlighted');
            elem.style.backgroundColor = null;
        });

        if (idElem !== null) {
            this.openSectionForElement(idElem);
            scrollAndHighlightElement(idElem);
        } else {
            const textElem = DOM.findText('.page-content > div > *', text);
            if (textElem) {
                this.openSectionForElement(textElem);
                scrollAndHighlightElement(textElem);
            }
        }
    }

    setupSectionAccordions() {
        this.sections = [...this.container.querySelectorAll('.cofe-page-section--coloured, .comp-section')];

        for (const section of this.sections) {
            const isWorthersSection = section.classList.contains('comp-section');
            if (isWorthersSection) {
                const header = section.querySelector('[data-section-toggle]');
                if (!header) {
                    continue;
                }

                header.addEventListener('click', event => {
                    if (event.target.closest('a, button, input, textarea, select, label, .popper')) {
                        return;
                    }
                    this.toggleSection(section);
                });

                header.addEventListener('keydown', event => {
                    if (event.key !== 'Enter' && event.key !== ' ') {
                        return;
                    }
                    event.preventDefault();
                    this.toggleSection(section);
                });

                this.syncWorthersSection(section);
                continue;
            }

            const shouldStartCollapsed = section.getAttribute('data-is-minimised') === 'true';
            section.classList.toggle('is-collapsed', shouldStartCollapsed);

            const toggleButton = section.querySelector('.cofe-page-section__toggle');
            const header = section.querySelector('.cofe-page-section__header');
            if (!toggleButton) {
                continue;
            }

            toggleButton.setAttribute('aria-expanded', shouldStartCollapsed ? 'false' : 'true');
            toggleButton.addEventListener('click', () => {
                this.toggleSection(section);
            });

            header?.addEventListener('click', event => {
                if (event.target.closest('a, button')) {
                    return;
                }
                this.toggleSection(section);
            });
        }
    }

    toggleSection(section) {
        if (section.classList.contains('comp-section')) {
            section.classList.toggle('-is-minimised');
            this.syncWorthersSection(section);
            return;
        }

        const isCollapsed = section.classList.toggle('is-collapsed');
        const toggleButton = section.querySelector('.cofe-page-section__toggle');
        if (toggleButton) {
            toggleButton.setAttribute('aria-expanded', isCollapsed ? 'false' : 'true');
        }
    }

    syncWorthersSection(section) {
        const toggleRegion = section.querySelector('[data-section-toggle]');
        if (!toggleRegion) {
            return;
        }

        const isExpanded = !section.classList.contains('-is-minimised');
        toggleRegion.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
    }

    openSection(section) {
        if (!section || !section.classList.contains('is-collapsed')) {
            if (section?.classList.contains('comp-section')) {
                section.classList.remove('-is-minimised');
                this.syncWorthersSection(section);
            }
            return;
        }

        section.classList.remove('is-collapsed');
        const toggleButton = section.querySelector('.cofe-page-section__toggle');
        if (toggleButton) {
            toggleButton.setAttribute('aria-expanded', 'true');
        }
    }

    openSectionForElement(element) {
        const section = element?.closest('.cofe-page-section--coloured, .comp-section');
        if (section) {
            this.openSection(section);
        }
    }

    setupNavHighlighting() {
        const pageNav = document.querySelector('.sidebar-page-nav');

        // fetch all the headings.
        const headings = document.querySelector('.page-content').querySelectorAll('h1, h2, h3, h4, h5, h6');
        // if headings are present, add observers.
        if (headings.length > 0 && pageNav !== null) {
            addNavObserver(headings);
        }

        for (const heading of headings) {
            const section = heading.closest('.cofe-page-section--coloured, .comp-section');
            if (!section) {
                continue;
            }

            const navLink = pageNav?.querySelector(`a[href="#${heading.id}"]`);
            if (navLink) {
                navLink.addEventListener('click', () => {
                    this.openSection(section);
                });
            }
        }
    }

}
